import { PropsWithChildren, createContext, useContext, useMemo } from 'react';

import { useFlags } from 'launchdarkly-react-client-sdk';
import { useLocation } from 'react-router-dom';

import { useGlobalContext } from '~/lib/contexts/GlobalContext';
import { useGetAllProvidersV2Query } from '~/store/services/providers';

export const VIEWING_PROVIDER_PARAM = 'provider';

export type ProviderSupervisorContextValue = {
  viewingProviderId: string;
  viewingProvider: GetProviderByIdResponse;
  supervisees: GetProviderByIdResponse[];
  isSelf: boolean;
  isReadOnly: boolean;
  isLoadingSupervisees: boolean;
};

const ProviderSupervisorContext = createContext<
  ProviderSupervisorContextValue | undefined
>(undefined);

export const ProviderSupervisorProvider = ({ children }: PropsWithChildren) => {
  const { currentUser } = useGlobalContext();
  const { providerDashboardSupervisorView } = useFlags<LDFlags>();
  const location = useLocation();

  const requestedProviderId = useMemo(
    () => new URLSearchParams(location.search).get(VIEWING_PROVIDER_PARAM),
    [location.search],
  );

  const { data: superviseesData, isLoading: isLoadingSupervisees } =
    useGetAllProvidersV2Query(
      { 'supervisor-id': [currentUser.userId] },
      { skip: !providerDashboardSupervisorView },
    );

  const value = useMemo<ProviderSupervisorContextValue>(() => {
    const supervisees = superviseesData?.data ?? [];
    const requested = supervisees.find(
      (provider) => provider.userId === requestedProviderId,
    );
    const viewingProvider = requested ?? currentUser;
    const isSelf = viewingProvider.userId === currentUser.userId;

    return {
      viewingProviderId: viewingProvider.userId,
      viewingProvider,
      supervisees,
      isSelf,
      isReadOnly: !isSelf,
      isLoadingSupervisees,
    };
  }, [currentUser, superviseesData, isLoadingSupervisees, requestedProviderId]);

  return (
    <ProviderSupervisorContext.Provider value={value}>
      {children}
    </ProviderSupervisorContext.Provider>
  );
};

export const useProviderSupervisor = (): ProviderSupervisorContextValue => {
  const context = useContext(ProviderSupervisorContext);
  if (!context) {
    throw new Error(
      'useProviderSupervisor must be used within a ProviderSupervisorProvider.',
    );
  }
  return context;
};
